import React, { useEffect, useState } from 'react';
import { Clock, TrendingUp, Loader2, Target } from 'lucide-react';

const HORIZONS = [
    { key: 'horizon_30', label: '30-Day', accent: 'border-red-500' },
    { key: 'horizon_60', label: '60-Day', accent: 'border-orange-500' },
    { key: 'horizon_90', label: '90-Day', accent: 'border-blue-500' },
];

const LOADING_PHASES = [
    'Ranking your top impacts by ROI exposure…',
    'Mapping urgent fixes to the 30-day window…',
    'Sequencing operational corrections for 60 days…',
    'Building the 90-day strategic roadmap…',
    'Finalizing — last touches…',
];

export default function VelocityPlan({ plan, loading }) {
    const [phaseIdx, setPhaseIdx] = useState(0);
    useEffect(() => {
        if (!loading) { setPhaseIdx(0); return; }
        const id = setInterval(() => setPhaseIdx(i => Math.min(i + 1, LOADING_PHASES.length - 1)), 2000);
        return () => clearInterval(id);
    }, [loading]);

    if (loading) {
        return (
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8">
                <div className="flex flex-col items-center text-center max-w-md mx-auto">
                    <div className="relative mb-4">
                        <div className="w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
                            <Target size={20} className="text-blue-600 dark:text-blue-400" />
                        </div>
                        <Loader2 size={48} className="animate-spin text-blue-500 absolute inset-0" />
                    </div>
                    <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 tracking-tight">
                        Generating Velocity Plan
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400 mt-1.5 leading-relaxed min-h-[2.5em]">
                        {LOADING_PHASES[phaseIdx]}
                    </div>
                    <div className="flex items-center gap-1 mt-4">
                        {LOADING_PHASES.map((_, i) => (
                            <div key={i} className={`h-1 rounded-full transition-all duration-300 ${
                                i <= phaseIdx
                                    ? 'w-6 bg-blue-500'
                                    : 'w-3 bg-slate-200 dark:bg-slate-700'
                            }`} />
                        ))}
                    </div>
                </div>
            </div>
        );
    }
    if (!plan) {
        return (
            <div className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
                <div className="text-sm text-slate-500 dark:text-slate-400">Velocity plan not generated yet.</div>
            </div>
        );
    }

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {HORIZONS.map(h => {
                const actions = plan[h.key] || [];
                return (
                    <div key={h.key} className={`rounded-lg border-l-4 ${h.accent} bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 p-4`}>
                        <div className="flex items-center gap-2 mb-3">
                            <Clock size={16} className="text-slate-500 dark:text-slate-400" />
                            <div className="font-semibold text-slate-800 dark:text-slate-100">{h.label}</div>
                            <span className="ml-auto text-xs text-slate-500 dark:text-slate-400">{actions.length} action{actions.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div className="space-y-3">
                            {actions.length === 0 && (
                                <div className="text-xs text-slate-400 dark:text-slate-500 italic">No actions scheduled for this horizon.</div>
                            )}
                            {actions.map((a, i) => (
                                <div key={i} className="border-t border-slate-100 dark:border-slate-800 pt-3 first:border-0 first:pt-0">
                                    <div className="font-medium text-sm text-slate-800 dark:text-slate-100">{a.title}</div>
                                    <div className="text-xs text-slate-600 dark:text-slate-300 mt-1 leading-relaxed">{a.detail}</div>
                                    <div className="flex items-center gap-3 mt-2 text-xs">
                                        <span className="inline-flex items-center gap-1 text-green-700 dark:text-green-400 font-medium">
                                            <TrendingUp size={12} /> +{a.score_impact} pts
                                        </span>
                                        <span className="text-slate-500 dark:text-slate-400">{a.roi_protection}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}
